import React, { useEffect, useState } from "react";
import QRCode from "react-qr-code";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, QrCode, X } from "lucide-react";
import { api, formatError, fmtDateTime } from "../../lib/api";
import { AdminHeader, AdminModal, AField, toLocalInput, fromLocalInput } from "../../components/AdminLayout";
import MapConfirm from "../../components/MapConfirm";
import ColorPicker from "../../components/ColorPicker";

const BLANK = { title: "", instructor: "", location: "", credit_cost: 1, capacity: 12, level: "All levels", starts_at: "", duration_min: 60, color: "#9D4EDD" };

export default function AdminClasses() {
  const [items, setItems] = useState([]);
  const [modal, setModal] = useState(null);
  const [qr, setQr] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/classes").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const setDate = (i, v) => setModal((m) => ({ ...m, dates: m.dates.map((d, j) => (j === i ? v : d)) }));
  const addDate = () => setModal((m) => ({ ...m, dates: [...(m.dates || []), ""] }));
  const removeDate = (i) => setModal((m) => ({ ...m, dates: m.dates.filter((_, j) => j !== i) }));

  const save = async () => {
    setBusy(true);
    const base = {
      title: modal.title, instructor: modal.instructor, location: modal.location || "", credit_cost: parseInt(modal.credit_cost, 10) || 1,
      capacity: parseInt(modal.capacity, 10) || 1, level: modal.level, duration_min: parseInt(modal.duration_min, 10) || 60,
      color: modal.color,
    };
    try {
      if (modal.id) {
        await api.put(`/admin/classes/${modal.id}`, { ...base, starts_at: modal.starts_at ? fromLocalInput(modal.starts_at) : undefined });
        toast.success("Class saved");
      } else {
        const dates = (modal.dates || []).filter(Boolean);
        if (dates.length === 0) { toast.error("Add at least one date"); setBusy(false); return; }
        for (const d of dates) await api.post("/admin/classes", { ...base, starts_at: fromLocalInput(d) });
        toast.success(`${dates.length} class${dates.length > 1 ? "es" : ""} created`);
      }
      setModal(null); load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const del = async (c) => {
    if (!window.confirm(`Delete ${c.title}? Bookings will be removed.`)) return;
    try { await api.delete(`/admin/classes/${c.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const openNew = () => setModal({ ...BLANK, dates: [toLocalInput(new Date().toISOString())] });

  return (
    <div>
      <AdminHeader title="Classes" subtitle={`${items.length} scheduled`}
        action={<button onClick={openNew} data-testid="admin-add-class" className="flex items-center gap-2 bg-[#D4AF37] text-black font-semibold rounded-full px-4 py-2.5 text-sm active:scale-95 transition-transform"><Plus size={16} /> New class</button>} />

      <div className="grid sm:grid-cols-2 gap-4">
        {items.map((c) => (
          <div key={c.id} data-testid={`admin-class-${c.id}`} className="rounded-2xl border border-white/10 bg-white/5 p-5">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="font-display text-xl font-semibold">{c.title}</h3>
                <p className="text-[#A19DB0] text-xs mt-0.5">{c.instructor} · {c.level}</p>
              </div>
              <span className="text-xs bg-[#9D4EDD]/20 text-[#c79bff] px-2.5 py-1 rounded-full">{c.credit_cost} cr</span>
            </div>
            <p className="text-[#A19DB0] text-sm mt-2">{fmtDateTime(c.starts_at)} · {c.duration_min}min</p>
            <p className="text-[#A19DB0] text-xs mt-1">{c.booked_count}/{c.capacity} booked</p>
            <div className="flex gap-2 mt-4">
              <button onClick={() => setModal({ ...c, starts_at: toLocalInput(c.starts_at) })} data-testid={`edit-class-${c.id}`} className="flex items-center gap-1.5 bg-white/5 rounded-full px-3 py-1.5 text-sm hover:bg-white/10"><Pencil size={14} /> Edit</button>
              <button onClick={() => setQr(c)} data-testid={`qr-class-${c.id}`} className="flex items-center gap-1.5 bg-white/5 rounded-full px-3 py-1.5 text-sm hover:bg-white/10"><QrCode size={14} className="text-[#D4AF37]" /> QR</button>
              <button onClick={() => del(c)} data-testid={`delete-class-${c.id}`} className="flex items-center gap-1.5 bg-white/5 rounded-full px-3 py-1.5 text-sm text-[#E57373] hover:bg-white/10"><Trash2 size={14} /> Delete</button>
            </div>
          </div>
        ))}
      </div>

      {modal && (
        <AdminModal open onOpenChange={() => setModal(null)} title={modal.id ? "Edit class" : "New class"} onSubmit={save} busy={busy}>
          <AField label="Title" value={modal.title} onChange={(e) => setModal({ ...modal, title: e.target.value })} data-testid="class-title" />
          <AField label="Instructor" value={modal.instructor} onChange={(e) => setModal({ ...modal, instructor: e.target.value })} data-testid="class-instructor" />
          <AField label="Location" value={modal.location || ""} onChange={(e) => setModal({ ...modal, location: e.target.value })} data-testid="class-location" placeholder="e.g. Rhythm Room, Clarke Quay, Singapore" />
          <MapConfirm query={modal.location} testidPrefix="class-map" />
          <div className="grid grid-cols-2 gap-3">
            <AField label="Credit cost" type="number" value={modal.credit_cost} onChange={(e) => setModal({ ...modal, credit_cost: e.target.value })} data-testid="class-cost" />
            <AField label="Capacity" type="number" value={modal.capacity} onChange={(e) => setModal({ ...modal, capacity: e.target.value })} data-testid="class-capacity" />
            <AField label="Duration (min)" type="number" value={modal.duration_min} onChange={(e) => setModal({ ...modal, duration_min: e.target.value })} data-testid="class-duration" />
            <AField label="Level" value={modal.level} onChange={(e) => setModal({ ...modal, level: e.target.value })} data-testid="class-level" />
          </div>
          {modal.id ? (
            <AField label="Starts at" type="datetime-local" value={modal.starts_at} onChange={(e) => setModal({ ...modal, starts_at: e.target.value })} data-testid="class-starts" />
          ) : (
            <div>
              <label className="text-xs text-[#A19DB0]">Dates — add several to create multiple sessions at once</label>
              {(modal.dates || []).map((d, i) => (
                <div key={i} className="flex gap-2 mt-1">
                  <input type="datetime-local" value={d} onChange={(e) => setDate(i, e.target.value)} data-testid={`class-date-${i}`} className="flex-1 bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-sm outline-none focus:border-[#D4AF37]" />
                  {modal.dates.length > 1 && <button type="button" onClick={() => removeDate(i)} data-testid={`class-remove-date-${i}`} className="text-[#E57373] px-3">✕</button>}
                </div>
              ))}
              <button type="button" onClick={addDate} data-testid="class-add-date" className="text-[#D4AF37] text-sm mt-2">+ Add another date</button>
            </div>
          )}
          <ColorPicker value={modal.color} onChange={(v) => setModal((m) => ({ ...m, color: v }))} />
        </AdminModal>
      )}

      {qr && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-6" onClick={() => setQr(null)} data-testid="class-qr-modal">
          <div className="relative bg-[#140F1E] border border-white/10 rounded-3xl p-8 text-center max-w-xs" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setQr(null)} className="absolute top-3 right-3 text-[#A19DB0] hover:text-white"><X size={18} /></button>
            <p className="font-display text-xl font-semibold mb-1">{qr.title}</p>
            <p className="text-[#A19DB0] text-xs mb-5">Members scan to check in</p>
            <div className="bg-white p-4 rounded-2xl inline-block">
              <QRCode value={JSON.stringify({ ref_type: "class", ref_id: qr.id })} size={200} fgColor="#0D0B14" />
            </div>
            <p className="text-[#A19DB0] text-xs mt-4">CLASS-{qr.id.slice(0, 8)}</p>
          </div>
        </div>
      )}
    </div>
  );
}
